import React, { useRef, useEffect, useState } from 'react';
import { isWeb, isIOS } from '../../util/platform';
import { View, Text, Pressable, Animated, StyleSheet, LayoutAnimation, AccessibilityInfo } from 'react-native'
import { Feather } from '@expo/vector-icons';
import { useTranslation } from '../../i18n';

/**
 * CollapsibleSection - Animated collapsible container with V3 styling
 *
 * @param {string} title - Section title
 * @param {string} subtitle - Optional preview text shown when collapsed
 * @param {boolean} isExpanded - Whether section is expanded
 * @param {function} onToggle - Toggle callback
 * @param {React.ReactNode} children - Section content
 * @param {Object} style - Optional additional styles
 */
const CollapsibleSection = ({ title, subtitle, isExpanded, onToggle, children, style }) => {
  const { t } = useTranslation('analytics');

  const rotateAnim = useRef(new Animated.Value(isExpanded ? 1 : 0)).current;
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Listen for reduced motion preference (WCAG AAA)
  useEffect(() => {
    AccessibilityInfo.isReduceMotionEnabled().then(setPrefersReducedMotion);
    const subscription = AccessibilityInfo.addEventListener(
      'reduceMotionChanged',
      setPrefersReducedMotion
    );
    return () => subscription.remove();
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) {
      // Instant transition when reduced motion is preferred
      rotateAnim.setValue(isExpanded ? 1 : 0);
    } else {
      // Animate chevron rotation
      Animated.spring(rotateAnim, {
        toValue: isExpanded ? 1 : 0,
        useNativeDriver: true,
        friction: 8,
        tension: 100,
      }).start();

      // Configure layout animation for content (iOS only - causes text glitches on Android)
      if (isIOS()) {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      }
    }
  }, [isExpanded, rotateAnim, prefersReducedMotion]);

  const chevronRotation = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '90deg'],
  });

  return (
    <View style={[styles.container, isExpanded && styles.expandedContainer, style]} collapsable={false}>
      <Pressable
        onPress={onToggle}
        style={({ pressed, hovered, focused }) => [
          styles.header,
          isWeb() && { cursor: 'pointer' },
          (hovered || focused) && styles.headerHovered,
          pressed && styles.headerPressed,
        ]}
        accessibilityRole="button"
        accessibilityState={{ expanded: isExpanded }}
        accessibilityLabel={`${title} section, ${isExpanded ? 'expanded' : 'collapsed'}`}
        accessibilityHint={t('toggleSectionHint')}
      >
        <View style={styles.titleContainer} collapsable={false}>
          <Text style={styles.title}>{title}</Text>
          {!isExpanded && subtitle && (
            <Text style={styles.subtitle}>{subtitle}</Text>
          )}
        </View>
        <Animated.View style={{ transform: [{ rotate: chevronRotation }] }}>
          <Feather name="chevron-right" size={20} color="#6B7280" />
        </Animated.View>
      </Pressable>

      {isExpanded && <View style={styles.content}>{children}</View>}
    </View>
  );
};

const styles = StyleSheet.create({
  // V3: Card container styling
  container: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    marginHorizontal: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#F0F0F0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  // V3: Gold accent on left when expanded
  expandedContainer: {
    borderLeftWidth: 3,
    borderLeftColor: '#FFC451',
  },
  // V3: Header with 44px+ touch target
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 16,
    minHeight: 56,
  },
  // V3: Visual feedback on hover/focus (web) and press
  headerHovered: {
    backgroundColor: '#F9FAFB',
  },
  headerPressed: {
    backgroundColor: '#F3F4F6',
  },
  // V3: Title container
  titleContainer: {
    flex: 1,
  },
  // V3: Title styling
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  // V3: Subtitle/preview text (shown when collapsed)
  subtitle: {
    fontSize: 13,
    color: '#6B7280',
    marginTop: 2,
  },
  // Content area with padding
  content: {
    paddingHorizontal: 4,
    paddingBottom: 16,
  },
});

export default CollapsibleSection;
